import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen w-full relative overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, #ff9a56 0%, #ff6b8a 25%, #c44eff 50%, #7b68ee 75%, #00bfff 100%)`,
            backgroundSize: '400% 400%',
            animation: 'gradientShift 15s ease infinite'
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10 p-6 flex items-center justify-between">
        <Link to="/" className="text-2xl font-bold tracking-tight text-black hover:opacity-80 transition-opacity">
          Spooker
        </Link>
        <Link 
          to="/login" 
          className="flex items-center gap-2 text-sm text-neutral-700 hover:text-black transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Terug naar inloggen
        </Link>
      </header>

      {/* Content */}
      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-120px)] px-4 py-8">
        <div className="w-full max-w-3xl">
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
            <div className="p-8 md:p-12">
              <h1 className="text-3xl font-bold text-neutral-900 mb-6">
                Privacybeleid
              </h1>
              
              <div className="prose prose-neutral max-w-none">
                <p className="text-neutral-600 mb-6">
                  Laatst bijgewerkt: 21 februari 2026
                </p>

                <section className="mb-8">
                  <h2 className="text-xl font-semibold text-neutral-800 mb-3">1. Inleiding</h2>
                  <p className="text-neutral-600 leading-relaxed">
                    Spooker B.V. ("wij", "ons" of "onze") respecteert uw privacy en zet zich in voor de bescherming van uw persoonsgegevens. Dit privacybeleid legt uit hoe wij uw gegevens verzamelen, gebruiken en beschermen wanneer u onze diensten gebruikt.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-xl font-semibold text-neutral-800 mb-3">2. Welke gegevens verzamelen wij</h2>
                  <p className="text-neutral-600 leading-relaxed mb-3">
                    Wij verzamelen de volgende categorieën persoonsgegevens:
                  </p>
                  <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4">
                    <li>Contactgegevens (naam, e-mailadres, telefoonnummer)</li>
                    <li>Bedrijfsgegevens (bedrijfsnaam, KvK-nummer, sector)</li>
                    <li>Accountgegevens (gebruikersnaam, wachtwoord)</li>
                    <li>Gebruiksgegevens (loginactiviteit, platformgebruik)</li>
                    <li>Cybersecurity assessmentgegevens</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-xl font-semibold text-neutral-800 mb-3">3. Hoe wij uw gegevens gebruiken</h2>
                  <p className="text-neutral-600 leading-relaxed mb-3">
                    Wij gebruiken uw gegevens voor:
                  </p>
                  <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4">
                    <li>Het leveren en verbeteren van onze diensten</li>
                    <li>Het uitvoeren van cybersecurity assessments</li>
                    <li>Communicatie over uw account en onze diensten</li>
                    <li>Het voldoen aan wettelijke verplichtingen</li>
                    <li>Het koppelen met leveranciers (alleen met uw toestemming)</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-xl font-semibold text-neutral-800 mb-3">4. Gegevensdeling</h2>
                  <p className="text-neutral-600 leading-relaxed">
                    Wij delen uw gegevens alleen met derden wanneer dit noodzakelijk is voor onze dienstverlening, wanneer u hier toestemming voor heeft gegeven, of wanneer wij hiertoe wettelijk verplicht zijn. Bij het gebruik van de marketplace worden gegevens alleen gedeeld met door u geselecteerde leveranciers.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-xl font-semibold text-neutral-800 mb-3">5. Beveiliging</h2>
                  <p className="text-neutral-600 leading-relaxed">
                    Wij nemen passende technische en organisatorische maatregelen om uw gegevens te beschermen tegen ongeautoriseerde toegang, verlies of misbruik. Dit omvat encryptie, toegangscontrole en regelmatige beveiligingsaudits.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-xl font-semibold text-neutral-800 mb-3">6. Uw rechten</h2>
                  <p className="text-neutral-600 leading-relaxed mb-3">
                    U heeft het recht om:
                  </p>
                  <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4">
                    <li>Inzage te vragen in uw persoonsgegevens</li>
                    <li>Correctie van onjuiste gegevens te verzoeken</li>
                    <li>Verwijdering van uw gegevens te verzoeken</li>
                    <li>Bezwaar te maken tegen verwerking</li>
                    <li>Uw gegevens over te dragen (dataportabiliteit)</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-xl font-semibold text-neutral-800 mb-3">7. Contact</h2>
                  <p className="text-neutral-600 leading-relaxed">
                    Voor vragen over dit privacybeleid of om uw rechten uit te oefenen, kunt u contact met ons opnemen via privacy@spooker.nl.
                  </p>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 p-6 flex items-center justify-between text-sm text-neutral-600">
        <span>© 2026 Spooker</span>
        <div className="flex gap-4">
          <Link to="/privacy" className="text-neutral-900 font-medium">Privacy</Link>
          <Link to="/terms" className="hover:text-neutral-900">Voorwaarden</Link>
        </div>
      </footer>

      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
};

export default PrivacyPage;
